import React, { useState } from 'react';
import { usePageSeo } from '../hooks/usePageSeo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Building2, Users, FileText, Handshake, DollarSign, BarChart3, MapPin, Shield, CheckCircle2, ArrowRight, ClipboardCheck, Linkedin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LINKEDIN_PAGE_URL = 'https://www.linkedin.com/company/civic-rentals-llc/';

const Landing = () => {
  const navigate = useNavigate();
  const [demoForm, setDemoForm] = useState({ name: '', email: '', organization: '', phone: '' });
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/demo-request`, demoForm);
      toast.success('Demo request submitted! We\'ll contact you within 24 hours.');
      setDemoForm({ name: '', email: '', organization: '', phone: '' });
    } catch (error) {
      console.error('Demo request error:', error);
      toast.error('Failed to submit demo request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success('Message sent! We\'ll get back to you soon.');
      setContactForm({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      icon: BarChart3,
      title: 'STR Market Overview',
      description: 'Maintain a clearer picture of rental trends in your area, supporting both day-to-day program operations and longer-term policy planning.'
    },
    {
      icon: FileText,
      title: 'Program Operations & Compliance Tracking',
      description: 'A workspace for regulatory staff to organize, document, and support short-term rental program work across their communities.'
    },
    {
      icon: Shield,
      title: 'Documentation & Program Readiness',
      description: 'Organized documentation for the many needs of short-term rental program administration.'
    },
    {
      icon: Handshake,
      title: 'Partnership-Building & Responsible STR Participation',
      description: 'Support education and partnership efforts with property managers, hosts, and community partners.'
    },
    {
      icon: DollarSign,
      title: 'Revenue Program Support',
      description: 'Improve program clarity and follow-through to strengthen registration and lodging tax collection efforts.'
    },
    {
      icon: ClipboardCheck,
      title: 'Online Permitting & Licensing Support',
      description: 'Support STR registration programs with structured processes, minimal paperwork, and organized recordkeeping.'
    }
  ];

  const audiences = [
    { icon: Building2, text: 'City, County, State & National Authorities' },
    { icon: FileText, text: 'Clerks, Licensing & Permitting Offices' },
    { icon: Users, text: 'Community Planning & Program Operations Teams' },
    { icon: DollarSign, text: 'Finance and Revenue Departments' },
    { icon: MapPin, text: 'Tourism & Destination Organizations' }
  ];

  usePageSeo({
    title: 'Short-Term Rental Compliance & Registration Software for Governments | Civic Rentals LLC',
    description: 'Identify and analyze short-term rentals, manage registrations and permitting, conduct operator outreach, monitor market activity, and support revenue programs—all in one platform built specifically for local governments.',
    canonical: 'https://civicrentals.com',
    ogTitle: 'Civic Rentals LLC - Government Software for Short-Term Rental Tracking',
    ogDescription: 'Civic Rentals LLC helps governments understand and manage short-term rental activity with tools for tracking listings, compliance, permitting, and revenue support.',
    ogUrl: 'https://civicrentals.com',
    ogImage: 'https://civicrentals.com/favicon.png',
    twitterCard: 'summary',
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        {/* Background Image with Opacity */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_lodge-connect-2/artifacts/dd2l3r2d_ChatGPT%20Image%20Mar%2011%2C%202026%2C%2011_05_47%20AM.png"
            alt="City with STR locations"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        {/* Content */}
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center space-y-6">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 leading-tight">
              Short-Term Rental (STR)
              <br />
              <span className="text-blue-600">Compliance & Registration</span>
              <br />
              <span className="text-blue-600">Software for Governments</span>
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Oversee short-term rentals in your community more effectively with Civic Rentals LLC. Manage registrations and permitting, conduct operator outreach, monitor market trends, manage complaints, and increase your STR revenue—all in one platform built specifically for local governments. Civic Rentals LLC supports government both before and after adopting a short-term rental ordinance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button size="lg" className="text-lg px-8 py-6" onClick={() => document.getElementById('demo').scrollIntoView({ behavior: 'smooth' })}>
                Request a Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="text-lg px-8 py-6" onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-cyan-900">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white">How Civic Rentals LLC Can Help Governments</h2>
            <p className="text-xl text-cyan-100 max-w-2xl mx-auto">
              Comprehensive tools and insights to manage your STR program effectively
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-slate-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <CardHeader>
                    <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-slate-600 leading-relaxed">{feature.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* TEMPORARILY HIDDEN — About the Founder Section — uncomment to restore */}

      {/* Who It's For Section */}
      <section id="who" className="py-20 px-6 bg-blue-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900">Who Civic Rentals LLC Is Built For</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Supporting government teams at every level; from enforcement to tax offices
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {audiences.map((audience, index) => {
              const Icon = audience.icon;
              return (
                <div key={index} className="bg-white rounded-xl p-6 flex items-start gap-4 hover:shadow-md transition-shadow">
                  <div className="h-10 w-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5 text-teal-600" />
                  </div>
                  <p className="text-slate-700 font-medium leading-relaxed">{audience.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Demo Request Section */}
      <section id="demo" className="py-20 px-6 bg-gradient-to-br from-blue-600 to-teal-600">
        <div className="container mx-auto max-w-2xl">
          <Card className="border-slate-200 shadow-xl">
            <CardHeader className="text-center space-y-2">
              <CardTitle className="text-3xl font-bold">Request a Demo</CardTitle>
              <CardDescription className="text-base">
                See how Civic Rentals LLC can support your jurisdiction's STR program
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleDemoSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label htmlFor="demo-name" className="text-sm font-medium text-slate-700">Full Name *</label>
                  <Input
                    id="demo-name"
                    type="text"
                    placeholder="John Smith"
                    value={demoForm.name}
                    onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                    required
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="demo-email" className="text-sm font-medium text-slate-700">Email Address *</label>
                  <Input
                    id="demo-email"
                    type="email"
                    placeholder="john.smith@city.gov"
                    value={demoForm.email}
                    onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
                    required
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="demo-org" className="text-sm font-medium text-slate-700">Organization *</label>
                  <Input
                    id="demo-org"
                    type="text"
                    placeholder="City of Springfield"
                    value={demoForm.organization}
                    onChange={(e) => setDemoForm({ ...demoForm, organization: e.target.value })}
                    required
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="demo-phone" className="text-sm font-medium text-slate-700">Phone Number</label>
                  <Input
                    id="demo-phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={demoForm.phone}
                    onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })}
                    className="h-12"
                  />
                </div>
                <Button type="submit" size="lg" className="w-full h-12 text-base" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Request Demo'}
                  {!isSubmitting && <ArrowRight className="ml-2 h-5 w-5" />}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-teal-700">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="bg-white rounded-lg px-6 border border-slate-200">
              <AccordionTrigger className="text-left font-semibold hover:text-blue-600">
                How does Civic Rentals LLC help with STR compliance?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                Civic Rentals LLC helps jurisdictions strengthen the administration of short-term rental programs through organized documentation, practical program support, and constructive engagement with property managers and hosts.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3" className="bg-white rounded-lg px-6 border border-slate-200">
              <AccordionTrigger className="text-left font-semibold hover:text-blue-600">
                Who typically uses Civic Rentals LLC?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                Civic Rentals LLC is designed for public-sector teams involved in short-term rental program administration, including local governments, state agencies, finance departments, licensing offices, planning teams, and other regulatory or operational staff.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4" className="bg-white rounded-lg px-6 border border-slate-200">
              <AccordionTrigger className="text-left font-semibold hover:text-blue-600">
                How long does implementation take?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                Implementation timelines vary based on jurisdiction size and program complexity. During your demo, we'll discuss your specific needs and provide a tailored implementation plan.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-gradient-to-br from-slate-800 to-slate-900 text-white">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold">Get In Touch</h2>
              <p className="text-slate-300 text-lg leading-relaxed">
                Have questions about how Civic Rentals LLC can support your jurisdiction? Our team is here to help you understand how we can work together.
              </p>
              <div className="space-y-4 pt-4">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-teal-400" />
                  <span className="text-slate-200">Personalized consultation</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-teal-400" />
                  <span className="text-slate-200">Custom implementation planning</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-teal-400" />
                  <span className="text-slate-200">Dedicated support team</span>
                </div>
              </div>
            </div>
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-2xl text-slate-900">Send Us a Message</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleContactSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <label htmlFor="contact-name" className="text-sm font-medium text-slate-700">Name *</label>
                    <Input
                      id="contact-name"
                      type="text"
                      placeholder="Your name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="contact-email" className="text-sm font-medium text-slate-700">Email *</label>
                    <Input
                      id="contact-email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="contact-message" className="text-sm font-medium text-slate-700">Message *</label>
                    <Textarea
                      id="contact-message"
                      placeholder="Tell us about your needs..."
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      required
                      rows={4}
                    />
                  </div>
                  <Button type="submit" className="w-full h-11" size="lg" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12 px-6 border-t border-slate-800">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img 
                  src="/logo.png" 
                  alt="Civic Rentals LLC Logo" 
                  className="h-8 w-auto object-contain"
                />
                <span className="text-xl font-bold text-white">Civic Rentals LLC</span>
              </div>
              <p className="text-sm leading-relaxed">
                Empowering jurisdictions with clarity and tools to manage short-term rental programs effectively.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#who" className="hover:text-white transition-colors">Who We Serve</a></li>
                <li><a href="#demo" className="hover:text-white transition-colors">Request Demo</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="/privacypolicy" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="/websiteTOS" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Connect</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#contact" className="hover:text-white transition-colors">Support</a></li>
                <li>
                  <a href={LINKEDIN_PAGE_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors inline-flex items-center gap-1.5">
                    <Linkedin className="h-3.5 w-3.5" />
                    LinkedIn
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-sm">
            <p>&copy; 2026 Civic Rentals LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;